import { useSearchParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { api, type ApiResponse } from "~/lib/api";
import { usePeriod } from "~/lib/period";

interface DashboardSummary {
  dateRange: { from: string; to: string };
  totalDevelopers: number;
  totalTeams: number;
  totalPrsMerged: { value: number; changePercent: number };
  totalStoryPoints: { value: number; changePercent: number };
  averageTimeToMerge: { value: number; changePercent: number };
  averageCycleTime: { value: number; changePercent: number };
  topMetrics: Array<{
    label: string;
    value: string;
    trend: string;
    trendValue: string;
  }>;
  // Extended data for charts
  prTrend?: Array<{ date: string; value: number }>;
  prSizeDistribution?: { small: number; medium: number; large: number };
  topContributors?: Array<{ name: string; value: number }>;
  storyPointsTrend?: Array<{ date: string; value: number }>;
}

interface WorkCategoryBreakdown {
  categories: Array<{
    category: string;
    label: string;
    color: string;
    count: number;
    percentage: number;
  }>;
  total: number;
  linkedToJira: number;
  linkedPercentage: number;
}

interface StoryToCodeMetrics {
  summary: {
    totalLinkedIssues: number;
    issuesWithStoryPoints: number;
    totalStoryPoints: number;
    totalLinesOfCode: number;
    totalPrs: number;
    totalReviews: number;
  };
  metrics: {
    avgLinesPerStoryPoint: number;
    avgPrsPerTicket: number;
    avgReviewCyclesPerTicket: number;
    avgFullCycleTime: number;
    avgFullCycleTimeDays: number;
  };
  insights: {
    effortCalibration: string;
    complexityIndicator: string;
    reviewHealth: string;
  };
}

interface WorkCategoryTrendData {
  trend: Array<{
    date: string;
    feature: number;
    bug: number;
    tech_debt: number;
    docs: number;
    release: number;
    infra: number;
    other: number;
    total: number;
  }>;
  categories: Array<{
    category: string;
    label: string;
    color: string;
  }>;
}

export function useDashboardData() {
  const [searchParams] = useSearchParams();
  const { range } = usePeriod();
  const teamId = searchParams.get("teamId") || "";

  const queryParams = new URLSearchParams({ range });
  if (teamId) queryParams.set("teamId", teamId);
  const queryString = queryParams.toString();

  const { data, isLoading, error } = useQuery({
    queryKey: ["dashboard", range, teamId],
    queryFn: () =>
      api.get<ApiResponse<DashboardSummary>>(
        `/metrics/dashboard?${queryString}`,
      ),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const { data: workCategoriesData } = useQuery({
    queryKey: ["work-categories", range, teamId],
    queryFn: () =>
      api.get<ApiResponse<WorkCategoryBreakdown>>(
        `/metrics/work-categories?${queryString}`,
      ),
    staleTime: 5 * 60 * 1000,
  });

  const { data: workCategoryTrendData } = useQuery({
    queryKey: ["work-categories-trend", range, teamId],
    queryFn: () =>
      api.get<ApiResponse<WorkCategoryTrendData>>(
        `/metrics/work-categories/trend?${queryString}`,
      ),
    staleTime: 5 * 60 * 1000,
  });

  // Story-to-code follows the page period and the team filter
  const storyToCodeParams = new URLSearchParams({ range });
  if (teamId) storyToCodeParams.set("teamId", teamId);

  const { data: storyToCodeData } = useQuery({
    queryKey: ["story-to-code", range, teamId],
    queryFn: () =>
      api.get<ApiResponse<StoryToCodeMetrics>>(
        `/metrics/story-to-code?${storyToCodeParams.toString()}`,
      ),
    staleTime: 5 * 60 * 1000,
  });

  const summary = data?.data;
  const workCategories = workCategoriesData?.data;
  const workCategoryTrend = workCategoryTrendData?.data;
  const storyToCode = storyToCodeData?.data;

  // Transform API data to chart-friendly format
  const stats = {
    prsMerged: summary?.totalPrsMerged.value ?? 0,
    prsMergedChange: summary?.totalPrsMerged.changePercent ?? 0,
    storyPoints: summary?.totalStoryPoints.value ?? 0,
    storyPointsChange: summary?.totalStoryPoints.changePercent ?? 0,
    avgTimeToMerge: summary?.averageTimeToMerge.value ?? 0,
    avgTimeToMergeChange: summary?.averageTimeToMerge.changePercent ?? 0,
    avgCycleTime: summary?.averageCycleTime.value ?? 0,
    avgCycleTimeChange: summary?.averageCycleTime.changePercent ?? 0,
  };

  // Only real series. The server doesn't send prTrend today, so the page
  // hides that figure rather than drawing invented numbers.
  const prTrend = summary?.prTrend ?? [];

  const prSizeDistribution = summary?.prSizeDistribution
    ? [
        { name: "Small (<100)", value: summary.prSizeDistribution.small },
        { name: "Medium (100-500)", value: summary.prSizeDistribution.medium },
        { name: "Large (>500)", value: summary.prSizeDistribution.large },
      ]
    : [];

  const topContributors = summary?.topContributors ?? [];

  const workCategoryBreakdown =
    workCategories?.categories.map((c) => ({
      name: c.label,
      category: c.category,
      value: c.count,
      percentage: c.percentage,
    })) ?? [];

  // Story points trend from API
  const storyPointsTrend = summary?.storyPointsTrend ?? [];

  return {
    stats,
    prTrend,
    prSizeDistribution,
    topContributors,
    workCategoryBreakdown,
    workCategoryStats: workCategories
      ? {
          total: workCategories.total,
          linkedToJira: workCategories.linkedToJira,
          linkedPercentage: workCategories.linkedPercentage,
        }
      : null,
    workCategoryTrend: workCategoryTrend ?? null,
    storyToCode: storyToCode ?? null,
    storyPointsTrend,
    isLoading,
    error,
  };
}
